import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    TableContainer,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    FormControlLabel,
    Checkbox,
    List,
    ListItem,
    CircularProgress,
    Tabs,
    Tab,
    RadioGroup,
    Radio,
    FormLabel
} from '@mui/material';
import { Edit, Trash2, Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import { getData, postData, putData, deleteData } from '../../Webservice/Webservice';

const RoleManagement = () => {
    const [selectedTab, setSelectedTab] = useState('admin');
    const [roles, setRoles] = useState([]);
    const [availableMenus, setAvailableMenus] = useState([]);
    const [open, setOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        id: null,
        name: '',
        short: '',
        description: '',
        roleFor: 'admin',
        menus: [] // Array of menu _IDs
    });

    const fetchRoles = async () => {
        try {
            const token = sessionStorage.getItem('token') || localStorage.getItem('token');
            const response = await getData(`role/list?roleFor=${selectedTab}`, null, `Bearer ${token}`);
            if (response && response.roles) {
                setRoles(response.roles);
            } else if (Array.isArray(response)) {
                setRoles(response);
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
            toast.error('Failed to load roles');
        }
    };

    const fetchMenus = async (menuFor = null) => {
        try {
            const target = menuFor || formData.roleFor || selectedTab;
            const token = sessionStorage.getItem('token') || localStorage.getItem('token');
            const response = await getData(`menu/list?menuFor=${target}`, null, `Bearer ${token}`);
            if (Array.isArray(response)) {
                setAvailableMenus(response);
            }
        } catch (error) {
            console.error('Error fetching menus:', error);
        }
    };

    useEffect(() => {
        fetchRoles();
        fetchMenus(selectedTab);
    }, [selectedTab]);

    const handleOpen = (roleForEdit = null) => {
        if (roleForEdit) {
            setIsEditing(true);
            setFormData({
                id: roleForEdit._id,
                name: roleForEdit.name.replace(/_/g, ' '), // unformat for display
                short: roleForEdit.short || '',
                description: roleForEdit.description || '',
                roleFor: roleForEdit.roleFor || 'admin',
                menus: roleForEdit.menus ? roleForEdit.menus.map(m => m._id || m) : []
            });
            fetchMenus(roleForEdit.roleFor || 'admin');
        } else {
            setIsEditing(false);
            setFormData({
                id: null,
                name: '',
                short: '',
                description: '',
                roleFor: selectedTab,
                menus: []
            });
            fetchMenus(selectedTab);
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleMainMenuToggle = (menu) => {
        setFormData(prev => {
            let newMenus = [...(prev.menus || [])];
            const isSelected = newMenus.includes(menu._id);
            const allMenuIds = [menu._id, ...(menu.submenus ? menu.submenus.map(s => s._id) : [])];

            if (isSelected) {
                newMenus = newMenus.filter(id => !allMenuIds.includes(id));
            } else {
                allMenuIds.forEach(id => {
                    if (!newMenus.includes(id)) newMenus.push(id);
                });
            }
            return { ...prev, menus: newMenus };
        });
    };

    const handleSubMenuToggle = (subMenuId, parentMenuId) => {
        setFormData(prev => {
            let newMenus = [...(prev.menus || [])];
            if (newMenus.includes(subMenuId)) {
                newMenus = newMenus.filter(id => id !== subMenuId);
            } else {
                newMenus.push(subMenuId);
                if (!newMenus.includes(parentMenuId)) {
                    newMenus.push(parentMenuId);
                }
            }
            return { ...prev, menus: newMenus };
        });
    };

    const handleSave = async () => {
        if (!formData.name) {
            toast.error('Role name cannot be empty');
            return;
        }

        try {
            setLoading(true);
            const token = sessionStorage.getItem('token') || localStorage.getItem('token');
            const payload = {
                name: formData.name,
                short: formData.short,
                description: formData.description,
                roleFor: formData.roleFor,
                menus: JSON.stringify(formData.menus)
            };

            if (isEditing) {
                await putData(`role/update/${formData.id}`, payload, `Bearer ${token}`);
                toast.success('Role updated successfully');
            } else {
                await postData('role/add', payload, `Bearer ${token}`);
                toast.success('Role created successfully');
            }
            handleClose();
            fetchRoles();
        } catch (error) {
            console.error('Error saving role:', error);
            toast.error('Failed to save role');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this role?')) {
            try {
                const token = sessionStorage.getItem('token') || localStorage.getItem('token');
                await deleteData(`role/delete/${id}`, `Bearer ${token}`);
                toast.success('Role deleted successfully');
                fetchRoles();
            } catch (error) {
                toast.error('Failed to delete role');
            }
        }
    };

    return (
        <Box sx={{ p: 4, maxWidth: 1200, margin: '0 auto' }}>
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    Role Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<Plus />}
                    onClick={() => handleOpen()}
                    sx={{ bgcolor: 'rgb(255 209 2)', color: 'black', '&:hover': { bgcolor: '#e6bd00' } }}
                >
                    Create Role
                </Button>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)} textColor="primary" indicatorColor="primary">
                    <Tab label="Admin CRM Roles" value="admin" />
                    <Tab label="Warehouse Roles" value="warehouse" />
                </Tabs>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell><strong>Role Name</strong></TableCell>
                            <TableCell><strong>Short Code</strong></TableCell>
                            <TableCell><strong>Description</strong></TableCell>
                            <TableCell><strong>Assigned Menus</strong></TableCell>
                            <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {roles.map((role) => (
                            <TableRow key={role._id}>
                                <TableCell sx={{ textTransform: 'capitalize' }}>
                                    {role.name ? role.name.replace(/_/g, ' ') : '-'}
                                </TableCell>
                                <TableCell>{role.short || '-'}</TableCell>
                                <TableCell>{role.description || '-'}</TableCell>
                                <TableCell>
                                    {(role.menus && role.menus.length > 0) ? `${role.menus.length} Menus` : 'None'}
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => handleOpen(role)} size="small" color="primary">
                                        <Edit size={18} />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(role._id)} size="small" color="error">
                                        <Trash2 size={18} />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {roles.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">No roles found</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{isEditing ? 'Edit Role' : 'Create New Role'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                        <TextField
                            label="Role Name (e.g. Sales Manager)"
                            fullWidth
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                        <TextField
                            label="Short Code (e.g. SM)"
                            fullWidth
                            value={formData.short}
                            onChange={(e) => setFormData({ ...formData, short: e.target.value })}
                        />
                        <TextField
                            label="Description"
                            fullWidth
                            multiline
                            rows={2}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />

                        <FormControl component="fieldset">
                            <FormLabel component="legend">Role Category</FormLabel>
                            <RadioGroup
                                row
                                value={formData.roleFor}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setFormData({ ...formData, roleFor: val, menus: [] });
                                    fetchMenus(val);
                                }}
                            >
                                <FormControlLabel value="admin" control={<Radio />} label="Admin CRM" />
                                <FormControlLabel value="warehouse" control={<Radio />} label="Warehouse" />
                            </RadioGroup>
                        </FormControl>

                        <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600 }}>Assign Menus & Submenus</Typography>
                        <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflowY: 'auto' }}>
                            <List disablePadding>
                                {availableMenus.map((menu) => (
                                    <React.Fragment key={menu._id}>
                                        <ListItem disablePadding>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={formData.menus?.includes(menu._id) || false}
                                                        onChange={() => handleMainMenuToggle(menu)}
                                                    />
                                                }
                                                label={<strong>{menu.name}</strong>}
                                            />
                                        </ListItem>
                                        {/* Render submenus */}
                                        {menu.submenus && menu.submenus.map((sub) => (
                                            <ListItem key={sub._id} disablePadding sx={{ pl: 4 }}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={formData.menus?.includes(sub._id) || false}
                                                            onChange={() => handleSubMenuToggle(sub._id, menu._id)}
                                                            size="small"
                                                        />
                                                    }
                                                    label={sub.name}
                                                />
                                            </ListItem>
                                        ))}
                                    </React.Fragment>
                                ))}
                                {availableMenus.length === 0 && <Typography variant="body2" color="textSecondary">No menus available to assign.</Typography>}
                            </List>
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleClose} variant="outlined" color="inherit">Cancel</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={loading}
                        sx={{ bgcolor: 'rgb(255 209 2)', color: 'black', '&:hover': { bgcolor: '#e6bd00' } }}
                    >
                        {loading ? <CircularProgress size={24} /> : (isEditing ? 'Update Role' : 'Save Role')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RoleManagement;
